import { useUser } from '@clerk/nextjs';
import { hide, onHide, onShow, onUnreadCountChange, show, update } from '@intercom/messenger-js-sdk';
import { useEffect, useState } from 'react';
import useDraggableLauncher from '~/hooks/useDraggableLauncher';
import { shouldUseIntercom } from '~/hooks/useIntercomBoot';
import useRouteCheck from '~/hooks/useRouteCheck';
import { useUserContext } from '~/react_context/UserContext';

// Appearance — matched to the old Intercom launcher (black circle, white chat icon, red unread dot).
// LAUNCHER_COLOR is the one knob to tweak to the business's Intercom brand color (Intercom configures
// this server-side, so it isn't available in code).
const LAUNCHER_COLOR = '#000000';
const LAUNCHER_SHADOW = '0 1px 6px 0 rgba(0,0,0,0.06), 0 2px 32px 0 rgba(0,0,0,0.16)';

// Matches the rendered button (h-12 w-12 = 48px) so clamping keeps the whole button on-screen.
const LAUNCHER_SIZE = 48;

// Approximate Intercom messenger panel size, used to keep the opened panel fully on-screen.
const PANEL_WIDTH = 400;
const PANEL_MIN_HEIGHT = 520;
const VIEWPORT_MARGIN = 20;

// Gap left between the button and the panel so the panel opens above the button (keeping it clickable to close).
const LAUNCHER_PANEL_GAP = 16;

const DEFAULT_PADDING = 20; // Intercom's default corner offset.
const ELEVATED_PADDING = 90; // Lift above the bottom bars on /materials, /schedule, and the work orders drawer footer (was the old route-based vertical_padding).
const STORAGE_KEY = 'glazier:intercom-launcher-pos';

// Module-scoped bridge between the Intercom singleton and whichever launcher is currently mounted.
//
// The SDK exposes no off* methods and keeps only the last callback per event, so re-registering on every
// mount would leave Intercom holding the unmounted launcher's stale state setters (firing setState into a
// dead component) and would rely on undocumented replace-on-register behavior. Because DefaultLayout and
// JobLayout each render a launcher, navigating between them unmounts one and mounts another. Instead we
// register with Intercom exactly once and route each event to the active launcher, which registers itself
// on mount and clears itself on unmount.
type LauncherHandlers = {
	onHide: () => void;
	onShow: () => void;
	onUnreadCountChange: (count: number) => void;
};

let activeHandlers: LauncherHandlers | null = null;
let intercomCallbacksRegistered = false;

// Live mirror of the messenger's state, kept by the single Intercom registration below. It survives launcher
// unmount/remount (e.g. navigating between layouts), so a freshly-mounted launcher can seed its icon/badge
// from the real state instead of resetting to closed / zero until Intercom next fires an event. (onShow/onHide
// only fire on transitions, and onUnreadCountChange's fire-on-register only reaches our one registration, not
// later launchers.)
let isMessengerOpen = false;
let currentUnreadCount = 0;

/** Make `handlers` the active launcher. Returns a cleanup that deregisters them (unless already replaced). */
function registerLauncherHandlers(handlers: LauncherHandlers): () => void {
	activeHandlers = handlers;
	if (!intercomCallbacksRegistered) {
		intercomCallbacksRegistered = true;
		onHide(() => {
			isMessengerOpen = false;
			activeHandlers?.onHide();
		});
		onShow(() => {
			isMessengerOpen = true;
			activeHandlers?.onShow();
		});
		onUnreadCountChange((count: number) => {
			currentUnreadCount = count;
			activeHandlers?.onUnreadCountChange(count);
		});
	}
	return () => {
		// Only clear if a newer launcher hasn't already taken over (mount-before-unmount during navigation).
		if (activeHandlers === handlers) activeHandlers = null;
	};
}

/**
 * Custom, freely-draggable Intercom launcher. Replaces Intercom's native launcher (which lives in a
 * cross-origin iframe we cannot drag) — see `hide_default_launcher` in useIntercomBoot. Clicking toggles
 * the messenger; dragging repositions the button and re-anchors the messenger panel to the nearest side.
 *
 * Rendered last in each layout so its effects run after useIntercomBoot has initialized window.Intercom.
 */
export function DraggableIntercomLauncher() {
	const { user } = useUser();
	const { impersonatingUser } = useUserContext();
	const routeCheck = useRouteCheck();
	const isElevatedRoute =
		routeCheck.isExactFileRoutePath('/materials') ||
		routeCheck.isExactFileRoutePath('/schedule') ||
		routeCheck.isExactFileRoutePath('/jobs/[jobId]/work-orders');

	const [mounted, setMounted] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);

	// Mirrors useIntercomBoot: when Intercom isn't booted (test user / impersonation), don't touch the SDK
	// (its methods would just warn) and don't render the button.
	const isTestUser = Boolean(user?.publicMetadata.isTestUser);
	const intercomEnabled = shouldUseIntercom({ impersonatingUser, isTestUser });

	// Until the user drags the button, the default lifts it above the bottom bars on /materials, /schedule, and the work orders drawer footer.
	const { isLoaded, launcherSize, pointerHandlers, pos, style } = useDraggableLauncher({
		defaultPosition: { horizontalPadding: DEFAULT_PADDING, side: 'right', verticalPadding: isElevatedRoute ? ELEVATED_PADDING : DEFAULT_PADDING },
		launcherSize: LAUNCHER_SIZE,
		onPress: () => (isOpen ? hide() : show()),
		storageKey: STORAGE_KEY,
	});

	useEffect(() => setMounted(true), []);

	// Anchor the messenger panel above the button, opening it toward the side with the most space. The panel
	// can only align left/right and always opens upward, so `pos.side` (the half the button sits in) sends it
	// into the wider horizontal gap, and lifting the bottom anchor by the button height + a gap makes the panel
	// open fully above the button (leaving it visible and clickable to close). We clamp the anchor so the full
	// panel stays on-screen rather than opening clipped when the button is dragged near the top or center.
	useEffect(() => {
		if (!intercomEnabled || !isLoaded) return;
		const maxHorizontalPadding = Math.max(VIEWPORT_MARGIN, window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN);
		const maxVerticalPadding = Math.max(VIEWPORT_MARGIN, window.innerHeight - PANEL_MIN_HEIGHT);
		update({
			alignment: pos.side,
			horizontal_padding: Math.min(pos.horizontalPadding, maxHorizontalPadding),
			vertical_padding: Math.min(pos.verticalPadding + launcherSize + LAUNCHER_PANEL_GAP, maxVerticalPadding),
		});
	}, [intercomEnabled, isLoaded, launcherSize, pos.side, pos.horizontalPadding, pos.verticalPadding]);

	// Track open state (so a click can toggle) and unread count (to rebuild the badge we lose with the native launcher).
	useEffect(() => {
		if (!intercomEnabled) return;
		// Seed from the live messenger state, since the panel may already be open / have unreads from before this
		// launcher mounted (e.g. after navigating between layouts) and we won't get a fresh event just for mounting.
		setIsOpen(isMessengerOpen);
		setUnreadCount(currentUnreadCount);
		return registerLauncherHandlers({
			onHide: () => setIsOpen(false),
			onShow: () => setIsOpen(true),
			onUnreadCountChange: (count: number) => setUnreadCount(count),
		});
	}, [intercomEnabled]);

	if (!mounted || !intercomEnabled) return null;

	return (
		<button
			aria-label="Open support chat"
			className="fixed z-[2147483000] flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-white transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
			{...pointerHandlers}
			style={{ backgroundColor: LAUNCHER_COLOR, boxShadow: LAUNCHER_SHADOW, touchAction: 'none', ...style }}
			type="button"
		>
			{/* Exact icons from the old Intercom launcher: the messenger bubble when closed, the collapse chevron when open. */}
			{isOpen ? (
				<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
					<path
						clipRule="evenodd"
						d="M18.601 8.39897C18.269 8.06702 17.7309 8.06702 17.3989 8.39897L12 13.7979L6.60099 8.39897C6.26904 8.06702 5.73086 8.06702 5.39891 8.39897C5.06696 8.73091 5.06696 9.2691 5.39891 9.60105L11.3989 15.601C11.7309 15.933 12.269 15.933 12.601 15.601L18.601 9.60105C18.9329 9.2691 18.9329 8.73091 18.601 8.39897Z"
						fill="currentColor"
						fillRule="evenodd"
					/>
				</svg>
			) : (
				<svg className="h-6 w-6" fill="currentColor" viewBox="0 0 28 32" xmlns="http://www.w3.org/2000/svg">
					<path d="M28,32 C28,32 23.2863266,30.1450667 19.4727818,28.6592 L3.43749107,28.6592 C1.53921989,28.6592 0,27.0272 0,25.0144 L0,3.6448 C0,1.632 1.53921989,0 3.43749107,0 L24.5615088,0 C26.45978,0 27.9989999,1.632 27.9989999,3.6448 L27.9989999,22.0490667 L28,22.0490667 L28,32 Z M23.8614088,20.0181333 C23.5309223,19.6105242 22.9540812,19.5633836 22.5692242,19.9125333 C22.5392199,19.9392 19.5537934,22.5941333 13.9989999,22.5941333 C8.51321617,22.5941333 5.48178311,19.9584 5.4277754,19.9104 C5.04295119,19.5629428 4.46760991,19.6105095 4.13759108,20.0170667 C3.97913051,20.2124916 3.9004494,20.4673395 3.91904357,20.7249415 C3.93763774,20.9825435 4.05196575,21.2215447 4.23660523,21.3888 C4.37862552,21.5168 7.77411059,24.5386667 13.9989999,24.5386667 C20.2248893,24.5386667 23.6203743,21.5168 23.7623946,21.3888 C23.9467342,21.2215726 24.0608642,20.9827905 24.0794539,20.7254507 C24.0980436,20.4681109 24.0195551,20.2135019 23.8614088,20.0181333 Z" />
				</svg>
			)}
			{unreadCount > 0 && <span className="absolute right-1 top-1 h-3 w-3 rounded-full border-2 border-white bg-red-500" />}
		</button>
	);
}
